import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { toast } from 'sonner';
import { ChevronRight } from 'lucide-react';


const STAGES = [
    { key: 'new_leads', label: 'New Leads', color: '#002FA7' },
    { key: 'verified', label: 'Verified', color: '#6366F1' },
    { key: 'contacted', label: 'Contacted', color: '#F59E0B' },
    { key: 'engaged', label: 'Engaged', color: '#10B981' },
    { key: 'qualified', label: 'Qualified', color: '#00A86B' },
    { key: 'won', label: 'Won', color: '#059669' },
    { key: 'lost', label: 'Lost', color: '#FF3333' },
];

function ScoreBadge({ score }) {
    const color = score >= 70 ? '#00A86B' : score >= 40 ? '#F59E0B' : '#FF3333';
    return (
        <span className="mono text-[10px] font-bold px-1 py-0.5 border"
            style={{ color, borderColor: color, background: `${color}15` }}>
            {score}
        </span>
    );
}

function LeadCard({ lead, onMoveStage }) {
    const [showMenu, setShowMenu] = useState(false);
    return (
        <div
            className="bg-white border border-neutral-200 p-3 mb-2 hover:border-neutral-300 transition-colors cursor-pointer relative"
            data-testid={`kanban-card-${lead.id}`}
        >
            <div className="flex items-start justify-between gap-1 mb-2">
                <p className="text-xs font-semibold text-neutral-900 leading-tight line-clamp-2 flex items-center gap-1.5 flex-wrap">
                    {lead.name}
                    {lead.is_manual && (
                        <span className="text-[8px] font-bold uppercase tracking-wider px-1 py-0.5 border border-emerald-300 bg-emerald-50 text-emerald-700 rounded-none flex-shrink-0" data-testid={`kanban-manual-badge-${lead.id}`}>
                            Manual
                        </span>
                    )}
                </p>
                <button
                    onClick={e => { e.stopPropagation(); setShowMenu(p => !p); }}
                    className="text-neutral-300 hover:text-neutral-600 flex-shrink-0"
                    data-testid={`card-menu-${lead.id}`}
                >
                    <ChevronRight size={12} />
                </button>
            </div>

            {showMenu && (
                <div className="absolute right-0 top-6 bg-white border border-neutral-200 shadow-sm z-10 w-40" data-testid={`stage-menu-${lead.id}`}>
                    {STAGES.map(s => (
                        <button
                            key={s.key}
                            onClick={e => { e.stopPropagation(); onMoveStage(lead.id, s.key); setShowMenu(false); }}
                            className="block w-full text-left text-xs px-3 py-2 hover:bg-neutral-50 transition-colors font-medium"
                            style={{ color: s.color }}
                        >
                            {s.label}
                        </button>
                    ))}
                </div>
            )}

            <p className="text-[10px] text-neutral-400 mb-2 line-clamp-1">{lead.category}</p>

            <div className="flex items-center justify-between">
                <ScoreBadge score={lead.score ?? 0} />
                <div className="flex gap-1">
                    {(lead.triggers || []).slice(0, 1).map((t) => (
                        <span key={t.type} className="text-[9px] px-1 py-0.5 bg-orange-50 text-orange-700 border border-orange-200 font-bold uppercase">
                            {t.type === 'no_website' ? 'NO WEB' : t.type === 'low_rating' ? 'LOW RTG' : 'LOW REV'}
                        </span>
                    ))}
                </div>
            </div>

            {lead.email && (
                <p className="text-[10px] text-neutral-400 mt-2 truncate">{lead.email}</p>
            )}
        </div>
    );
}

export default function Pipeline() {
    const navigate = useNavigate();
    const [leads, setLeads] = useState([]);
    const [stats, setStats] = useState({});
    const [loading, setLoading] = useState(true);
    const [sourceFilter, setSourceFilter] = useState('');

    const loadData = async () => {
        try {
            const params = { limit: 500 };
            if (sourceFilter === 'true') params.is_manual = true;
            if (sourceFilter === 'false') params.is_manual = false;
            const [leadsRes, statsRes] = await Promise.all([
                api.get(`/leadgen/leads`, { params }),
                api.get(`/leadgen/pipeline/stats`),
            ]);
            setLeads(leadsRes.data.leads || []);
            setStats(statsRes.data.stages || {});
        } catch { } finally { setLoading(false); }
    };

    useEffect(() => { loadData(); }, [sourceFilter]);

    const handleMoveStage = async (leadId, newStage) => {
        // Optimistic update
        setLeads(prev => prev.map(l => l.id === leadId ? { ...l, pipeline_stage: newStage } : l));
        try {
            const { data } = await api.put(`/leadgen/leads/${leadId}/stage`, { stage: newStage });
            if (data?.client_created && data?.client_id) {
                toast.success('Lead won! Client created in BMHQ CRM.', {
                    action: {
                        label: 'View client',
                        onClick: () => navigate(`/clients/${data.client_id}`),
                    },
                    duration: 8000,
                });
            }
            loadData();
        } catch { loadData(); toast.error('Failed to move lead'); }
    };

    const getLeadsForStage = (stageKey) => leads.filter(l => l.pipeline_stage === stageKey);

    if (loading) {
        return (
            <div className="p-8 animate-pulse">
                <div className="h-8 bg-neutral-100 w-48 mb-6" />
                <div className="flex gap-4">
                    {[...Array(7)].map((_, i) => <div key={i} className="h-64 bg-neutral-100 w-52 flex-shrink-0" />)}
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full" data-testid="pipeline-page">
            {/* Header */}
            <div className="border-b border-neutral-200 bg-white px-6 py-4 flex-shrink-0 flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-black tracking-tight text-neutral-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                        Pipeline
                    </h1>
                    <p className="text-xs text-neutral-500 mt-0.5">
                        {leads.length} total leads · Click the arrow on any card to move it
                    </p>
                </div>
                <select
                    value={sourceFilter}
                    onChange={e => setSourceFilter(e.target.value)}
                    className="text-xs border border-neutral-200 bg-neutral-50 px-2 py-1.5 rounded-none focus:outline-none"
                    data-testid="pipeline-manual-import-filter"
                >
                    <option value="">All Sources</option>
                    <option value="true">Manual / Imported</option>
                    <option value="false">Scraped (Outscraper)</option>
                </select>
            </div>

            {/* Kanban Board */}
            <div className="flex-1 overflow-x-auto overflow-y-hidden p-6">
                <div className="flex gap-4 h-full min-h-0" style={{ minWidth: 'max-content' }}>
                    {STAGES.map(stage => {
                        const stageLeads = getLeadsForStage(stage.key);
                        return (
                            <div
                                key={stage.key}
                                className="kanban-col flex flex-col"
                                data-testid={`pipeline-col-${stage.key}`}
                            >
                                {/* Column Header */}
                                <div className="flex items-center justify-between px-3 py-2.5 border-b border-neutral-200 flex-shrink-0">
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: stage.color }} />
                                        <span className="text-xs font-bold uppercase tracking-wider text-neutral-700">{stage.label}</span>
                                    </div>
                                    <span
                                        className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                                        style={{ background: `${stage.color}20`, color: stage.color }}
                                        data-testid={`stage-count-${stage.key}`}
                                    >
                                        {stageLeads.length}
                                    </span>
                                </div>

                                {/* Cards */}
                                <div className="flex-1 overflow-y-auto p-2">
                                    {stageLeads.length === 0 ? (
                                        <div className="text-center py-6 text-xs text-neutral-300 uppercase tracking-wider">
                                            Empty
                                        </div>
                                    ) : stageLeads.map(lead => (
                                        <LeadCard
                                            key={lead.id}
                                            lead={lead}
                                            onMoveStage={handleMoveStage}
                                        />
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
